import {LayoutTraversal} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/layout/LayoutTraversal';
import {
    GlassLayer,
    HeavyPanelContent,
    IPanelDatum,
    Panel,
    PanelContentFactoryFn,
    PanelGroup,
    PanelHeader,
    PanelId,
    PanelKind,
    PanelSet,
    PanelSetName,
    Section,
    SectionKind,
    SectionRun
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/PanelState';
import {VisualDesignerTab} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/WorkbenchState';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action} from 'mobx';
import * as React from 'react';

export enum ScreenRegion
{
    Content = 'Content',
    North = 'North',
    East = 'East',
    South = 'South',
    West = 'West'
}

export type PerspectiveName = PanelSetName;

export class Perspectives
{
    public static readonly FLOW_TAB:PerspectiveName = VisualDesignerTab.FlowTab.value() as PerspectiveName;
    public static readonly SURFACE_TAB:PerspectiveName = VisualDesignerTab.SurfaceTab.value() as PerspectiveName;
}

export class Perspective
{
    private _name:PerspectiveName;
    private _panelSet:PanelSet;
    private _regionToPanelGroup:Map<ScreenRegion, PanelGroup> = new Map();
    private _regionToSection:Map<ScreenRegion, Section> = new Map();

    private constructor(name:PerspectiveName)
    {
        this._name = name;
        this._setupDefaultLayout(name);
    }

    public getPanelGroupForRegion(region:ScreenRegion):PanelGroup
    {
        return this._regionToPanelGroup.get(region);
    }

    public get name():string
    {
        return this._name;
    }

    public get panelSet():PanelSet
    {
        return this._panelSet;
    }

    public hasSectionRun(run:SectionRun):boolean
    {
        return this.sectionRuns.findIndex((r:SectionRun) => (r.id === run.id)) !== -1;
    }

    public getAllUnpinnedOfKind(kind:PanelKind):Panel[]
    {
        const panels = [];

        for( let panel of this.panels )
        {
            if( panel.kind === kind && !panel.header.isPinned )
                panels.push(panel);
        }

        return panels;
    }

    public findPanelRegion(panel:Panel):ScreenRegion | null
    {
        const screenRegions = Array.from(this._regionToPanelGroup.keys());
        const panelGroup = panel.panelGroup;

        let foundRegion:ScreenRegion = null;

        for( let screenRegion of screenRegions )
        {
            const group = this._regionToPanelGroup.get(screenRegion);
            if( group.id === panelGroup.id )
                foundRegion = screenRegion;
        }

        return foundRegion;
    }

    public findFirstPanelByKind(kind:PanelKind):Panel | null
    {
        return this.panels.find((p:Panel) => (p.kind === kind));
    }

    public findPanelById(panelId:PanelId):Panel | null
    {
        return this.panels.find((p:Panel) => (p.id === panelId));
    }

    public findPanelByDatum(datum:IPanelDatum):Panel | null
    {
        return this.panels.find((p:Panel) => (p.datum && p.datum.isEqualTo(datum)));
    }

    public get sectionRuns():SectionRun[]
    {
        const rootRun = this._panelSet.rootRun;
        const retSectionRuns:SectionRun[] = [];

        LayoutTraversal.traverseSectionRuns(rootRun, (run:SectionRun) =>
        {
            retSectionRuns.push(run);
        });

        return retSectionRuns;
    }

    public get panelGroups():PanelGroup[]
    {
        const rootRun = this._panelSet.rootRun;
        const retGroups:PanelGroup[] = [];

        LayoutTraversal.traversePanelGroups(rootRun, (group:PanelGroup) =>
        {
            retGroups.push(group);
        });

        return retGroups;
    }

    public get panels():Panel[]
    {
        const retPanels:Panel[] = [];

        this.panelGroups.map((g:PanelGroup) =>
        {
            g.panels.map((p:Panel) =>
            {
                retPanels.push(p);
            });
        });

        return retPanels;
    }

    public get sections():Section[]
    {
        const rootRun = this._panelSet.rootRun;
        const retSections:Section[] = [];

        LayoutTraversal.traverseSections(rootRun, (section:Section) =>
        {
            retSections.push(section);
        });

        return retSections;
    }

    @bind
    public static defaultWrap(content:React.ReactElement<any>):PanelContentFactoryFn
    {
        return () => (content);
    }

    @bind
    private _getLen(name:PerspectiveName, region:ScreenRegion):number
    {
        if( name === Perspectives.FLOW_TAB )
        {
            switch( region )
            {
                case ScreenRegion.West:
                    return 294;
                case ScreenRegion.East:
                    return 324;
                case ScreenRegion.South:
                    return 300;
            }
        }
        else if( name === Perspectives.SURFACE_TAB )
        {
            switch( region )
            {
                case ScreenRegion.West:
                    return 294;
                case ScreenRegion.East:
                    return 324;
                case ScreenRegion.South:
                    return 300;
            }
        }

        return NaN;
    }

    @bind
    private _setupDefaultLayout(name:PerspectiveName):void
    {
        const westPanels = PanelGroup.of([]);
        const eastPanels = PanelGroup.of([]);
        const southPanels = PanelGroup.of([]);
        const content = PanelGroup.of([]);

        const westSection = Section.ofLength(this._getLen(name, ScreenRegion.West), SectionKind.PanelArea, westPanels);
        const southSection = Section.ofLength(this._getLen(name, ScreenRegion.South), SectionKind.PanelArea, southPanels);
        const contentSection = Section.stretch(SectionKind.Content, content);

        const middleSections = Section.stretch(SectionKind.Content, SectionRun.vertical(
            [
                contentSection,
                southSection
            ]));

        const eastSection = Section.ofLength(this._getLen(name, ScreenRegion.East), SectionKind.PanelArea, eastPanels);

        const rootRun = SectionRun.horizontal
        (
            [
                westSection,
                middleSections,
                eastSection
            ]
        );

        this._panelSet = new PanelSet(name, rootRun);
        this._regionToPanelGroup.set(ScreenRegion.West, westPanels);
        this._regionToPanelGroup.set(ScreenRegion.East, eastPanels);
        this._regionToPanelGroup.set(ScreenRegion.South, southPanels);
        this._regionToPanelGroup.set(ScreenRegion.Content, content);

        this._regionToSection.set(ScreenRegion.West, westSection);
        this._regionToSection.set(ScreenRegion.East, eastSection);
        this._regionToSection.set(ScreenRegion.South, southSection);
        this._regionToSection.set(ScreenRegion.Content, contentSection);
    }

    public resizeScreenRegion(region:ScreenRegion, newValue:number):void
    {
        const section = this.findSection(region);
        if( !section )
        {
            console.warn(`Warning: cannot find section for screen region: ${region}`, region);
            return;
        }

        section.runSpec.value = newValue;
    }

    public findSection(region:ScreenRegion):Section | null
    {
        return this._regionToSection.get(region);
    }

    public isSectionInRegion(section:Section, region:ScreenRegion):boolean
    {
        return this.findSection(region) === section;
    }

    @action.bound
    public setContent(content:React.ReactElement<any>):Perspective
    {
        const contentPanelGroup = this._findPanelGroup(ScreenRegion.Content);
        if( !contentPanelGroup )
            return this;

        const panelKind:PanelKind = null;
        contentPanelGroup.panels = [Panel.ofContent(panelKind, contentPanelGroup, Perspective.defaultWrap(content))];
        return this;
    }

    public getPanelGroup(region:ScreenRegion):PanelGroup | null
    {
        const panelGroup = this._findPanelGroup(region);
        return panelGroup;
    }

    @action.bound
    public addPanel(region:ScreenRegion, kind:PanelKind, title:string, element:React.ReactElement<any>):Perspective
    {
        const panelGroup = this._findPanelGroup(region);
        if( !panelGroup )
            return this;

        if( region === ScreenRegion.South )
            panelGroup.panels.push(Panel.ofHeader(IdUtil.newGUID(), kind, panelGroup, PanelHeader.withTitle(title), Perspective.defaultWrap(element)));
        else
            panelGroup.panels.push(Panel.of(kind, panelGroup, title, Perspective.defaultWrap(element)));

        return this;
    }

    @action.bound
    public addPanelDirect(region:ScreenRegion, kind:PanelKind, panel:Panel):Perspective
    {
        const panelGroup = this._findPanelGroup(region);
        if( !panelGroup )
            return this;

        panelGroup.panels.push(panel);
        return this;
    }

    @action.bound
    public addHeavyPanelContents(panelGroup:PanelGroup, forPanelKind:PanelKind, element:React.ReactElement<any>):Perspective
    {
        if( !panelGroup.heavyPanelContents )
            panelGroup.heavyPanelContents = [];

        panelGroup.heavyPanelContents.push(HeavyPanelContent.create(forPanelKind, element));
        return this;
    }

    private _findPanelGroup(region:ScreenRegion):PanelGroup | null
    {
        const panelGroup = this._regionToPanelGroup.get(region);

        if( !panelGroup )
        {
            console.error(`Error: panel group for region not found, region: ${region}`);
            return null;
        }

        return panelGroup;
    }

    @action.bound
    public addGlass(glassElement:React.ReactElement<any>):Perspective
    {
        const glassLayer = GlassLayer.of(glassElement);
        this._panelSet.glassLayers.push(glassLayer);

        return this;
    }

    public static named(perspectiveName:string):Perspective
    {
        return new Perspective(perspectiveName);
    }
}
